/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { logout } from '@/lib/api-client'
import { clearAuth, getAuthToken } from '@/lib/auth-client'
import { LoggingOutOverlay } from '@/components/auth/LoggingOutOverlay'

type LogoutOptions = {
  onBeforeNavigate?: () => void
}

type LogoutContextValue = {
  isLoggingOut: boolean
  logoutWithOverlay: (options?: LogoutOptions) => Promise<void>
}

const LogoutContext = createContext<LogoutContextValue | null>(null)

export function LogoutProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const inFlightRef = useRef<Promise<void> | null>(null)

  const logoutWithOverlay = useCallback(
    async (options?: LogoutOptions) => {
      if (inFlightRef.current) return inFlightRef.current

      setIsLoggingOut(true)

      const task = (async () => {
        const token = getAuthToken()

        try {
          if (token) await logout(token)
        } catch (err) {
          console.warn('logout api failed', err)
        } finally {
          clearAuth()
          try {
            options?.onBeforeNavigate?.()
          } catch (err) {
            console.warn('logout pre-navigation hook failed', err)
          }
          navigate('/', { replace: true })
          setTimeout(() => setIsLoggingOut(false), 0)
        }
      })()

      inFlightRef.current = task.finally(() => {
        inFlightRef.current = null
      })

      return inFlightRef.current
    },
    [navigate]
  )

  const value = useMemo(
    () => ({ isLoggingOut, logoutWithOverlay }),
    [isLoggingOut, logoutWithOverlay]
  )

  if (location.pathname.startsWith('/admin')) {
    return <>{children}</>
  }

  return (
    <LogoutContext.Provider value={value}>
      {children}
      <LoggingOutOverlay open={isLoggingOut} />
    </LogoutContext.Provider>
  )
}

export function useLogout(): LogoutContextValue {
  const context = useContext(LogoutContext)
  if (!context) {
    throw new Error('useLogout must be used within a LogoutProvider')
  }
  return context
}
